import { createContext } from 'react';
import { isNodeEdgeMapperLike, isValidBusinessObject } from '@iotv/datamodel-core';
import { getTypeFilter } from '@iotv/core-services';
import { EntityControlReducer, isValidDCT } from '../../Entity/Reducers/EnitityControlReducer';
import { isDeviceEnabledType } from '../../../../../../data/TypeHelpers';
/** RSE 2025 JAN — modified to allow null `EntityGroup` so this reducer and context can be re-used for `EntityProgrameSet` Control tab */
const debug = process.env.REACT_APP_DEBUG && false;
const isLoadContextAction = (x) => {
    const c = x;
    return (!!c &&
        c.type === 'LoadContext' &&
        !!c.value &&
        isNodeEdgeMapperLike(c.value.EntityGroupContext) &&
        (c.value.EntityGroup === null || isValidBusinessObject(c.value.EntityGroup)));
};
const isUpdateAction = (x) => {
    const c = x;
    return !!c && c.type === 'Update' && !!c.value;
};
const isAddTransactionsAction = (x) => {
    const c = x;
    return !!c && c.type === 'AddTransactions' && !!c.value && c.value.dcts instanceof Array && c.value.dcts.length > 0 && c.value.dcts.every(isValidDCT);
};
/**
 * Note: `EntityStates` gets updated (`AddTransactionsAction`) independently from `EntityGroupContext` and `EntityGroup` (`LoadContext`)
 *
 * There is protection in `loadContextReducer` to prevent it resetting state if its' action is recieved after transactions are loaded
 *
 * When refactoring, or basing something else from this, avoid this conflict
 */
export const EntityGroupControlReducer = (prev, action) => {
    const update = { ...prev };
    if (isLoadContextAction(action)) {
        debug && console.log(`EntityGroupControlReducer got ${action.type}`);
        return loadContextReducer(prev, action);
    }
    else if (isUpdateAction(action)) {
        debug && console.log(`EntityGroupControlReducer got ${action.type}`);
        Object.assign(update, action);
        return update;
    }
    else if (isAddTransactionsAction(action)) {
        debug && console.log(`EntityGroupControlReducer got AddTransactionsAction ${action.type}`, action.value);
        const { entity, dcts } = action.value;
        const addTransactionsAction = { type: 'AddTransactions', value: { entity, dcts } };
        if (!update.EntityStates) {
            update.EntityStates = {};
        }
        const entityStateObject = { ...EntityControlReducer(update.EntityStates[entity.sk], addTransactionsAction), Entity: entity };
        update.EntityStates = { ...update.EntityStates, [entity.sk]: entityStateObject };
        debug && console.log(`EntityGroupControlReducer (AddTransactionsAction) ${action.type} updating EntityStates`, update.EntityStates);
        return update;
    }
    else {
        console.log(`WARN: unknown action type`, action);
        return update;
    }
};
const loadContextReducer = (prev, action) => {
    const update = { ...prev };
    const { EntityGroupContext, EntityGroup } = action.value;
    update.EntityGroupContext = EntityGroupContext;
    update.EntityGroup = EntityGroup;
    if (EntityGroup) {
        update.Entities = EntityGroupContext.getChildNodes(EntityGroup).filter(getTypeFilter(EntityGroup.supportedType ?? 'never'));
    }
    else {
        update.Entities = EntityGroupContext.getContainedItems().filter(isDeviceEnabledType);
    }
    /**
     * RSE 2025 02 — this is/was causing the disappearing transaction issue, look here for trouble
     */
    // if ( update.EntityStates && Object.keys( update.EntityStates ).length > 0 ) {
    //     update.EntityStates = Object.fromEntries(update.Entities.map((entity) => {
    //         const loadEntityAction: LoadEntityAction = { type: 'LoadEntity', value: { Entity: entity } };
    //         return [ entity.sk, EntityControlReducer({ Entity: null, FifoDCTs: null }, loadEntityAction) ]
    //     }))
    //     // throw new Error( `ERR: loadContextReducer is resetting existing EntityStates`)
    // }
    return update;
};
export const defaultState = {
    EntityGroup: null,
    Entities: null,
    EntityGroupContext: null,
    EntityStates: null,
};
export const EntityGroupControlContext = createContext({
    state: defaultState,
    dispatch: (a) => {
        throw new Error('Uninitalized EntityGroupControlReducer usage');
    },
});
